<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { notificationService } from '../services/notification.service'

const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const clubUuid = computed(() => authStore.initData?.club?.uuid || null)

// Data
const notifications = ref<any[]>([])
const loading = ref(false)
const unreadCount = ref(0)
const pagination = ref({
  currentPage: 1,
  perPage: 50,
  total: 0,
  lastPage: 1,
  from: 0,
  to: 0
})

const selectedNotifications = ref<Set<string>>(new Set())
const selectAll = ref(false)
const showActionsDropdown = ref(false)

// Fetch notifications from backend
const fetchNotifications = async () => {
  if (!clubUuid.value) return

  loading.value = true
  try {
    const [response, count] = await Promise.all([
      notificationService.getNotifications(
        clubUuid.value,
        pagination.value.currentPage,
        pagination.value.perPage
      ),
      notificationService.getUnreadCount(clubUuid.value)
    ])

    unreadCount.value = count

    // Map backend response to frontend format
    notifications.value = response.data.map((item: any) => ({
      uuid: item.uuid,
      title: item.title || t('notifications.notificationFallbackTitle'),
      content: item.content,
      date: item.created_at,
      read: item.is_read,
      button: item.button
    }))

    pagination.value = {
      currentPage: response.meta.current_page,
      perPage: response.meta.per_page,
      total: response.meta.total,
      lastPage: response.meta.last_page,
      from: (response.meta.current_page - 1) * response.meta.per_page + 1,
      to: Math.min(response.meta.current_page * response.meta.per_page, response.meta.total)
    }
  } catch (error: any) {
    console.error('Error fetching notifications:', error)
    toastStore.error(t('notifications.errorFetchingNotifications'))
  } finally {
    loading.value = false
  }
}

// Toggle select all
const toggleSelectAll = () => {
  if (selectAll.value) {
    notifications.value.forEach(notification => {
      selectedNotifications.value.add(notification.uuid)
    })
  } else {
    selectedNotifications.value.clear()
  }
}

// Toggle notification selection
const toggleNotificationSelection = (notificationUuid: string) => {
  if (selectedNotifications.value.has(notificationUuid)) {
    selectedNotifications.value.delete(notificationUuid)
  } else {
    selectedNotifications.value.add(notificationUuid)
  }

  selectAll.value = notifications.value.length > 0 &&
                    notifications.value.every(n => selectedNotifications.value.has(n.uuid))
}

const isNotificationSelected = (notificationUuid: string) => {
  return selectedNotifications.value.has(notificationUuid)
}

// Get notification URL
const getNotificationUrl = (notificationUuid: string) => {
  if (!clubUuid.value) return '#'
  return `/notifications/${clubUuid.value}/${notificationUuid}`
}

// Handle notification click (mark as read)
const handleNotificationClick = async (notificationUuid: string) => {
  if (!clubUuid.value) return

  try {
    // Mark notification as read when clicking
    await notificationService.markAsRead(clubUuid.value, [notificationUuid])

    // Update UI
    const notification = notifications.value.find(n => n.uuid === notificationUuid)
    if (notification) {
      notification.read = true
    }
  } catch (error: any) {
    console.error('Error marking notification as read:', error)
  }
}

// Toggle actions dropdown
const toggleActionsDropdown = () => {
  showActionsDropdown.value = !showActionsDropdown.value
}

// Mark as read
const markAsRead = async () => {
  if (selectedNotifications.value.size === 0) {
    toastStore.error(t('notifications.errorNoSelection'))
    return
  }

  if (!clubUuid.value) return

  try {
    await notificationService.markAsRead(
      clubUuid.value,
      Array.from(selectedNotifications.value)
    )

    toastStore.success(t('notifications.markedAsRead', { count: selectedNotifications.value.size }))

    // Update UI
    notifications.value.forEach(notification => {
      if (selectedNotifications.value.has(notification.uuid)) {
        notification.read = true
      }
    })

    selectedNotifications.value.clear()
    selectAll.value = false
    showActionsDropdown.value = false
  } catch (error: any) {
    console.error('Error marking notifications as read:', error)
    toastStore.error(t('notifications.errorMarkingAsRead'))
  }
}

// Mark as unread
const markAsUnread = async () => {
  if (selectedNotifications.value.size === 0) {
    toastStore.error(t('notifications.errorNoSelection'))
    return
  }

  if (!clubUuid.value) return

  try {
    await notificationService.markAsUnread(
      clubUuid.value,
      Array.from(selectedNotifications.value)
    )

    toastStore.success(t('notifications.markedAsUnread', { count: selectedNotifications.value.size }))

    // Update UI
    notifications.value.forEach(notification => {
      if (selectedNotifications.value.has(notification.uuid)) {
        notification.read = false
      }
    })

    selectedNotifications.value.clear()
    selectAll.value = false
    showActionsDropdown.value = false
  } catch (error: any) {
    console.error('Error marking notifications as unread:', error)
    toastStore.error(t('notifications.errorMarkingAsUnread'))
  }
}

// Pagination
const changePage = (page: number) => {
  pagination.value.currentPage = page
  fetchNotifications()
}

const prevPage = () => {
  if (pagination.value.currentPage > 1) {
    changePage(pagination.value.currentPage - 1)
  }
}

const nextPage = () => {
  if (pagination.value.currentPage < pagination.value.lastPage) {
    changePage(pagination.value.currentPage + 1)
  }
}

const changePerPage = () => {
  pagination.value.currentPage = 1
  fetchNotifications()
}

// Close dropdown when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.actions-dropdown-container')) {
    showActionsDropdown.value = false
  }
}

onMounted(() => {
  fetchNotifications()
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4">
      <!-- Page Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-900">
          {{ t('notifications.title') }} <span class="text-lg font-normal text-gray-600">({{ unreadCount }})</span>
        </h1>
      </div>

      <!-- Bulk Actions Bar -->
      <div class="mb-4 bg-white border-2 border-gray-300 rounded shadow-sm p-3 flex items-center gap-3">
        <input
          type="checkbox"
          id="selectAll"
          v-model="selectAll"
          @change="toggleSelectAll"
          class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        >
        <label for="selectAll" class="text-sm font-medium text-gray-700 cursor-pointer">{{ t('notifications.selectAll') }}</label>

        <div class="ml-auto relative actions-dropdown-container">
          <button
            @click="toggleActionsDropdown"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
          >
            {{ t('notifications.actions') }}
          </button>
          <div
            v-show="showActionsDropdown"
            class="absolute right-0 mt-2 w-56 bg-white border-2 border-gray-300 rounded shadow-md z-50"
          >
            <button @click="markAsRead" class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
              {{ t('notifications.markAsRead') }}
            </button>
            <button @click="markAsUnread" class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
              {{ t('notifications.markAsUnread') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p class="mt-2 text-gray-600">{{ t('notifications.loading') }}</p>
      </div>

      <!-- Desktop Table -->
      <div v-if="!loading" class="hidden md:block bg-white border-2 border-gray-300 rounded shadow-sm">
        <div style="overflow-x: auto; overflow-y: visible;">
          <table class="w-full text-sm">
            <thead class="bg-gray-100 border-b-2 border-gray-300">
              <tr>
                <th class="px-4 py-3 text-left font-semibold text-gray-700 w-12"></th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('notifications.notificationContent') }}</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('notifications.date') }}</th>
                <th class="px-4 py-3 text-right font-semibold text-gray-700 sticky right-0 bg-gray-100">{{ t('notifications.actionsColumn') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-if="notifications.length === 0">
                <td colspan="4" class="px-4 py-8 text-center text-gray-500">
                  {{ t('notifications.noNotifications') }}
                </td>
              </tr>
              <tr
                v-for="notification in notifications"
                :key="notification.uuid"
                class="group"
                :class="!notification.read ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'"
              >
                <td class="px-4 py-3">
                  <input
                    type="checkbox"
                    :checked="isNotificationSelected(notification.uuid)"
                    @change="toggleNotificationSelection(notification.uuid)"
                    class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  >
                </td>
                <td class="px-4 py-3">
                  <router-link
                    :to="getNotificationUrl(notification.uuid)"
                    @click="handleNotificationClick(notification.uuid)"
                    class="text-gray-900 hover:text-blue-600 hover:underline"
                    :class="!notification.read ? 'font-semibold' : ''"
                  >
                    {{ notification.title }}
                  </router-link>
                </td>
                <td class="px-4 py-3 text-gray-700">{{ notification.date }}</td>
                <td
                  class="px-4 py-3 sticky right-0 text-right"
                  :class="!notification.read ? 'bg-blue-50 group-hover:bg-blue-100' : 'bg-white group-hover:bg-gray-50'"
                >
                  <router-link
                    :to="getNotificationUrl(notification.uuid)"
                    @click="handleNotificationClick(notification.uuid)"
                    class="px-3 py-1.5 bg-blue-600 text-xs font-medium text-white rounded hover:bg-blue-700 border border-blue-600"
                  >
                    {{ t('notifications.view') }}
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Mobile List -->
      <div v-if="!loading" class="md:hidden space-y-2">
        <div v-if="notifications.length === 0" class="bg-white border-2 border-gray-300 rounded shadow-sm p-8 text-center text-gray-500">
          {{ t('notifications.noNotifications') }}
        </div>
        <div
          v-for="notification in notifications"
          :key="notification.uuid"
          class="bg-white border-2 border-gray-300 rounded shadow-sm p-3"
          :class="!notification.read ? 'bg-blue-50' : ''"
        >
          <div class="flex items-start gap-3">
            <input
              type="checkbox"
              :checked="isNotificationSelected(notification.uuid)"
              @change="toggleNotificationSelection(notification.uuid)"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1"
            >
            <div class="flex-1 min-w-0">
              <router-link
                :to="getNotificationUrl(notification.uuid)"
                @click="handleNotificationClick(notification.uuid)"
                class="text-sm text-gray-900 hover:text-blue-600 hover:underline"
                :class="!notification.read ? 'font-semibold' : ''"
              >
                {{ notification.title }}
              </router-link>
              <p class="text-xs text-gray-600 mt-1">{{ notification.date }}</p>
              <router-link
                :to="getNotificationUrl(notification.uuid)"
                @click="handleNotificationClick(notification.uuid)"
                class="inline-block mt-2 px-3 py-1.5 bg-blue-600 text-xs font-medium text-white rounded hover:bg-blue-700 border border-blue-600"
              >
                {{ t('notifications.view') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && pagination.total > 0" class="mt-4 flex justify-end items-center gap-4">
        <div class="flex items-center gap-2">
          <label for="perPage" class="text-sm text-gray-700">{{ t('notifications.perPage') }}</label>
          <select
            id="perPage"
            v-model.number="pagination.perPage"
            @change="changePerPage"
            class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white"
          >
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="75">75</option>
            <option :value="100">100</option>
          </select>
        </div>
        <div class="flex items-center gap-1">
          <button
            @click="prevPage"
            :disabled="pagination.currentPage === 1"
            class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            &laquo;
          </button>
          <button class="px-3 py-1 border-2 border-gray-900 bg-gray-900 text-white rounded text-sm font-medium">
            {{ pagination.currentPage }}
          </button>
          <button
            @click="nextPage"
            :disabled="pagination.currentPage === pagination.lastPage"
            class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            &raquo;
          </button>
        </div>
      </div>
    </div>
  </main>
</template>
